import { create, StoreApi } from 'zustand';

import {
  AssetItem,
  DisinheritedPersonItem,
  DistributionRecord,
  FulfillmentState,
  IWBeneficiary,
  IwEditingState,
  WitnessesItem,
} from '@/entities/interwills';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { objects } from '@/shared/utils/objects';
import { UnregBeneficiaryData } from '@/widgets/modals/containers/interwill/CreateBeneficiaryModalV2/types';

import { getEmptyIwBeneficiary } from '../helpers';

type IwEditingStore = IwEditingState & {
  updateTestator: (data: IWBeneficiary) => void;
  updateWitness: (index: number, data: WitnessesItem) => void;
  updateWitnesses: (data: WitnessesItem[]) => void;
  updateBeneficiary: (index: number, data: IWBeneficiary, clear?: boolean) => void;
  updateDisinheritedPerson: (index: number, data: DisinheritedPersonItem) => void;
  updateContingentBeneficiary: (data: IWBeneficiary) => void;
  updateAssets: (data: AssetItem[]) => void;
  updateDistribution: (data: DistributionRecord) => void;
  updateSpouse: (spouse: DtoId | undefined) => void;
  updateExecutor: (executor: WitnessesItem) => void;
  updateSubExecutor: (subExecutor: WitnessesItem) => void;
  updateFulfillment: (data: Partial<FulfillmentState>) => void;
  updateShariaLaws: (data: boolean) => void;
  updateHasSpouse: (data: boolean) => void;
  updateUnregBeneficiaries: (data: UnregBeneficiaryData[]) => void;
  updateIntroductionMessage: (data?: string) => void;
  removeWitness: (index: number) => void;
  removeBeneficiaries: (index: number) => void;
  removeContingentBeneficiary: () => void;
  removeDisinheritedPerson: (index: number) => void;
  removeExecutor: () => void;
  removeSubExecutor: () => void;
  setEditedEntities: (editedEntities: Record<DtoId, IWBeneficiary | WitnessesItem>) => void;
  removeEditedEntity: (id: DtoId) => void;
  setPdfDrafts: (pdfDrafts: Record<string, string>) => void;
  setLastSavedVersion: (version: number) => void;
  extractStateForCollection: () => Omit<IwEditingState, 'changeVersion' | 'lastSavedVersion'>;
  clear: () => void;
};

const getInitialState = (): IwEditingState => ({
  testator: { ...getEmptyIwBeneficiary(), primaryResidence: undefined },
  witnesses: [],
  beneficiaries: [],
  unregBeneficiaries: [],
  disinheritedPerson: [],
  assets: [],
  distribution: {},
  shariaLaws: false,
  hasSpouse: false,
  editedEntities: {},
  pdfDrafts: {},
  introductionMessage: undefined,
  contingentBeneficiary: undefined,
  executor: undefined,
  spouse: undefined,
  subExecutor: undefined,
  changeVersion: 1,
  lastSavedVersion: 1,

  fulfillmentState: {
    beneficiaries: false,
    executor: false,
    distribution: false,
    testator: false,
    witnesses: false,
    assets: false,
  },
});

export const updateWithVersion =
  (set: StoreApi<IwEditingState>['setState']) =>
  (fnOrObj: Partial<IwEditingState> | ((state: IwEditingState) => Partial<IwEditingState>)) => {
    set((state) => {
      const data = typeof fnOrObj === 'function' ? fnOrObj(state) : fnOrObj;

      return { ...data, changeVersion: state.changeVersion + 1 };
    });
  };

export const useIwEditingStore = create<IwEditingStore>((set, get) => {
  const update = updateWithVersion(set);
  return {
    ...getInitialState(),

    clear: () => {
      set({ ...getInitialState() });
    },

    updateTestator: (data) => {
      update((state) => ({
        testator: {
          ...getEmptyIwBeneficiary(),
          ...state.testator,
          ...data,
        },
        changeVersion: state.changeVersion + 1,
      }));
    },

    updateWitness: (index, data) => {
      update((state) => {
        const witnesses = [...state.witnesses];
        witnesses[index] = {
          ...getEmptyIwBeneficiary(),
          ...witnesses[index],
          ...data,
        };
        return { witnesses };
      });
    },

    updateWitnesses: (witnesses) => set({ witnesses }),

    updateDisinheritedPerson: (index, data) => {
      update((state) => {
        const disinheritedPerson = [...state.disinheritedPerson];
        disinheritedPerson[index] = {
          ...disinheritedPerson[index],
          ...data,
        };
        return { disinheritedPerson };
      });
    },

    updateContingentBeneficiary: (data) => {
      update((state) => {
        return {
          contingentBeneficiary: {
            ...getEmptyIwBeneficiary({ noCitizenship: true }),
            ...state.contingentBeneficiary,
            ...data,
          },
        };
      });
    },

    updateAssets: (assets) => update({ assets }),

    updateDistribution: (distribution) => update({ distribution }),

    updateSpouse: (spouse) => update({ spouse }),

    updateExecutor: (executor) => {
      update((state) => ({
        executor: {
          ...getEmptyIwBeneficiary(),
          ...state.executor,
          ...executor,
        },
      }));
    },

    updateSubExecutor: (subExecutor) => {
      update((state) => ({
        subExecutor: {
          ...getEmptyIwBeneficiary(),
          ...state.subExecutor,
          ...subExecutor,
        },
      }));
    },

    updateShariaLaws: (shariaLaws) => update({ shariaLaws }),

    updateHasSpouse: (hasSpouse) => update({ hasSpouse }),

    removeWitness: (index) => {
      update((state) => {
        return { witnesses: state.witnesses.filter((_, i) => i !== index) };
      });
    },

    removeBeneficiaries: (index) => {
      update((state) => {
        return { beneficiaries: state.beneficiaries.filter((_, i) => i !== index) };
      });
    },

    updateBeneficiary: (index, data, clear) => {
      update((state) => {
        const beneficiaries = [...state.beneficiaries];
        beneficiaries[index] = {
          ...getEmptyIwBeneficiary({ noCitizenship: true }),
          ...(clear ? {} : beneficiaries[index]),
          ...data,
        };
        return { beneficiaries };
      });
    },

    updateFulfillment: (data) => {
      set((state) => ({
        fulfillmentState: { ...state.fulfillmentState, ...data },
      }));
    },

    updateIntroductionMessage: (introductionMessage) => update({ introductionMessage }),

    removeContingentBeneficiary: () => update({ contingentBeneficiary: undefined }),

    removeDisinheritedPerson: (index) => {
      update((state) => {
        return { disinheritedPerson: state.disinheritedPerson.filter((_, i) => i !== index) };
      });
    },

    removeExecutor: () => update({ executor: undefined }),

    removeSubExecutor: () => update({ subExecutor: undefined }),

    updateUnregBeneficiaries: (unregBeneficiaries) => update({ unregBeneficiaries }),

    setEditedEntities: (editedEntities) => {
      set({
        editedEntities: {
          ...get().editedEntities,
          ...objects.copy(editedEntities),
        },
      });
    },

    removeEditedEntity: (id) => {
      const entities = get().editedEntities;
      const filteredEditedEntries = Object.entries(entities).filter(([key]) => key !== id);
      set({ editedEntities: Object.fromEntries(filteredEditedEntries) });
    },

    setPdfDrafts: (pdfDrafts) => set({ pdfDrafts }),

    setLastSavedVersion: (lastSavedVersion) => set({ lastSavedVersion }),

    extractStateForCollection: () => ({
      testator: get().testator,
      witnesses: get().witnesses,
      beneficiaries: get().beneficiaries,
      contingentBeneficiary: get().contingentBeneficiary,
      disinheritedPerson: get().disinheritedPerson,
      assets: get().assets,
      distribution: get().distribution,
      fulfillmentState: get().fulfillmentState,
      shariaLaws: get().shariaLaws,
      hasSpouse: get().hasSpouse,
      executor: get().executor,
      subExecutor: get().subExecutor,
      unregBeneficiaries: get().unregBeneficiaries,
      introductionMessage: get().introductionMessage,
      spouse: get().spouse,
      editedEntities: get().editedEntities,
      pdfDrafts: get().pdfDrafts,
    }),
  };
});
